import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Link } from "react-router-dom";
import { format, subDays } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  DollarSign,
  Handshake,
  Link2,
  AlertTriangle,
  Trophy,
  Sparkles,
  RefreshCw,
  Activity,
  ArrowUpDown,
} from "lucide-react";

import { MyRankTrendBanner } from "./MyRankTrendBanner";
import { KpiCard } from "./KpiCard";
import { OnboardingCompletionWidget } from "@/components/OnboardingCompletionWidget";
import OnboardingResumeBanner from "@/components/OnboardingResumeBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SILENT_THRESHOLD_MS = 7 * 24 * 60 * 60 * 1000;

const ADMIN_KPIS = [
  {
    key: "cash_collected", label: "CASH COLLECTED", icon: DollarSign, format: "currency",
    tooltip: "Money actually received. Lower than Booked Revenue when deals sit on payment plans.",
  },
  { key: "deals_closed", label: "TOTAL DEALS", icon: Handshake, format: "number" },
  {
    key: "revenue_generated", label: "BOOKED REVENUE", icon: Sparkles, format: "currency",
    tooltip: "Total contract value booked in the period (may include payment plans not yet fully collected).",
  },
];

export default function AdminView() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [users, setUsers] = useState([]);
  const [digest, setDigest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [compareOn, setCompareOn] = useState(true);

  const start_date = format(subDays(new Date(), 30), "yyyy-MM-dd");
  const end_date = format(new Date(), "yyyy-MM-dd");

  const fetchData = useCallback(async () => {
    if (!user?.account_id) return;
    setLoading(true);
    try {
      const statsParams = new URLSearchParams({ start_date, end_date });
      if (compareOn) statsParams.append("compare", "true");
      const lbUrl = compareOn ? `${API}/leaderboard?compare=true` : `${API}/leaderboard`;
      const [statsRes, lbRes, whRes, usersRes, digestRes] = await Promise.all([
        fetch(`${API}/dashboard/stats?${statsParams}`, { credentials: "include" }),
        fetch(lbUrl, { credentials: "include" }),
        fetch(`${API}/webhooks`, { credentials: "include" }),
        fetch(`${API}/users`, { credentials: "include" }),
        fetch(`${API}/accounts/${user.account_id}/weekly-wins/preview`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (lbRes.ok) setLeaderboard(await lbRes.json());
      if (whRes.ok) setWebhooks(await whRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (digestRes.ok) setDigest(await digestRes.json());
    } catch (e) {
      console.error("admin dashboard fetch failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user?.account_id, start_date, end_date, compareOn]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const activeWebhooks = webhooks.filter(w => w.active);
  const silentWebhooks = activeWebhooks.filter(w => {
    if (!w.last_fired_at) return true;
    return (Date.now() - new Date(w.last_fired_at).getTime()) > SILENT_THRESHOLD_MS;
  });
  const top3Climbers = leaderboard
    .filter(r => typeof r.rank_change === "number" && r.rank_change > 0)
    .sort((a, b) => b.rank_change - a.rank_change)
    .slice(0, 3);

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-dashboard-view">
      <OnboardingResumeBanner />
      <MyRankTrendBanner />

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-3xl font-bold text-white font-heading">Account Overview</h1>
          <p className="text-gray-400 mt-1 text-xs sm:text-sm">Last 30 days · {activeWebhooks.length} active integration{activeWebhooks.length === 1 ? "" : "s"} · {users.length} team member{users.length === 1 ? "" : "s"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant={compareOn ? "default" : "outline"}
            size="sm"
            data-testid="admin-compare-toggle"
            onClick={() => setCompareOn(!compareOn)}
            className={compareOn ? "bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-500" : "bg-white/5 border-white/10 text-white hover:bg-white/10"}
          >
            <ArrowUpDown className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">{compareOn ? "vs Prev Period" : "Compare"}</span>
            <span className="sm:hidden">{compareOn ? "On" : "Cmp"}</span>
          </Button>
          <Button asChild variant="outline" size="sm" data-testid="admin-manage-integrations-btn" className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <Link to="/settings?tab=integrations"><Link2 className="w-4 h-4 mr-2" />Integrations</Link>
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Account-wide KPIs */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {[...Array(3)].map((_, i) => (
            <div key={`admin-kpi-skel-${i}`} className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5 animate-pulse">
              <div className="h-4 w-24 bg-white/10 rounded mb-4" />
              <div className="h-10 w-32 bg-white/10 rounded" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4">
          {ADMIN_KPIS.map(kpi => (
            <KpiCard
              key={kpi.key}
              label={kpi.label}
              value={stats?.[kpi.key]}
              icon={kpi.icon}
              format={kpi.format}
              tooltip={kpi.tooltip}
              compareOn={compareOn && stats?.compare}
              change={stats?.changes?.[kpi.key]}
              prevValue={stats?.previous?.[kpi.key]}
            />
          ))}
        </div>
      )}

      {/* Integration Health + Weekly Wins preview side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="admin-integration-health">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-400" />
              <h3 className="text-white font-semibold">Integration Health</h3>
            </div>
            <span className="text-xs text-gray-500">{activeWebhooks.length} active</span>
          </div>
          {activeWebhooks.length === 0 ? (
            <p className="text-sm text-gray-500">No webhooks connected yet. <Link to="/settings?tab=integrations" className="text-emerald-400 hover:underline">Add one →</Link></p>
          ) : silentWebhooks.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-emerald-400">
              <Sparkles className="w-4 h-4" />
              All channels fired within the last 7 days.
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3 text-amber-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">{silentWebhooks.length} silent channel{silentWebhooks.length === 1 ? "" : "s"} (&gt;7 days)</span>
              </div>
              <ul className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="admin-silent-webhooks">
                {silentWebhooks.slice(0, 5).map(w => (
                  <li key={w.webhook_id} className="flex items-center gap-2 text-xs">
                    <span className={`w-2 h-2 rounded-full ${w.provider === "discord" ? "bg-indigo-400" : "bg-green-400"}`} />
                    <span className="text-gray-300 capitalize">{w.provider}</span>
                    <span className="text-gray-500 truncate">{w.webhook_url}</span>
                  </li>
                ))}
              </ul>
              <Link to="/settings?tab=integrations" className="inline-block mt-3 text-xs text-emerald-400 hover:underline">Review channels →</Link>
            </>
          )}
        </div>

        <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="admin-weekly-wins-preview">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <h3 className="text-white font-semibold">Weekly Wins Preview</h3>
            </div>
            <Link to="/settings?tab=organization" className="text-xs text-emerald-400 hover:underline">Configure</Link>
          </div>
          {!digest || digest.climbers?.length === 0 ? (
            <p className="text-sm text-gray-500">No qualifying climbers this period. The digest will still fire with totals + silent-channel alerts.</p>
          ) : (
            <div className="space-y-2">
              {digest.climbers.slice(0, 3).map((c, idx) => (
                <div key={c.user_id} className="flex items-center gap-3 py-1.5">
                  <Trophy className={`w-4 h-4 ${idx === 0 ? "text-amber-400" : "text-gray-500"}`} />
                  <span className="text-sm text-white flex-1">{c.name}</span>
                  <span className="text-xs text-gray-400">#{c.prev_rank} → #{c.current_rank}</span>
                  <span className="text-xs font-semibold text-emerald-400">+{c.rank_change}</span>
                </div>
              ))}
              {digest.totals && (
                <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs text-gray-400">
                  <span>Week totals: ${(digest.totals.cash || 0).toLocaleString()} / {digest.totals.deals || 0} deals</span>
                  <span className={digest.totals.cash_pct >= 0 ? "text-emerald-400" : "text-red-400"}>
                    {digest.totals.cash_pct >= 0 ? "+" : ""}{digest.totals.cash_pct}% wow
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Top 3 climbers / top performers mini leaderboard */}
      {compareOn && top3Climbers.length > 0 && (
        <div className="bg-[#1A1A1A] border border-white/5 rounded-xl p-5" data-testid="admin-top-climbers">
          <div className="flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-amber-400" />
            <h3 className="text-white font-semibold">Top Climbers (30-day Period-over-Period)</h3>
          </div>
          <div className="space-y-2">
            {top3Climbers.map(r => (
              <div key={r.user_id} className="flex items-center gap-3 py-1.5">
                <span className="flex-1 text-sm text-white">{r.name}</span>
                <span className="text-xs text-gray-400">#{r.prev_rank} → #{r.current_rank}</span>
                <span className="text-xs font-semibold text-emerald-400">+{r.rank_change} spots</span>
                <span className="text-xs text-gray-300 min-w-[5rem] text-right">${(r.cash || 0).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Onboarding completion across each role-track */}
      <OnboardingCompletionWidget />
    </div>
  );
}
